import { cn } from '@/util/cn';
import { useFrontendFeature } from '@/util/functions/frontendFeatureToggles';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import { tv, VariantProps } from 'tailwind-variants';

// eslint-disable-next-line react-refresh/only-export-components
export const tableVariants = tv({
  slots: {
    container: '',
    table: '',
    head: '',
    row: '',
    header: '',
    body: '',
    cell: '',
    scrollWrapper: '',
    scrollIndicator: '',
    gradientLeft: '',
    gradientRight: ''
  },
  variants: {
    variant: {
      striped: {
        container: 'relative w-full max-w-full rounded-md bg-white shadow-sm border',
        scrollWrapper: 'relative overflow-x-scroll',
        body: '[&_tr:last-child]:border-0',
        row: 'hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors',
        header: 'bg-gray-100 [&_tr]:border-b',
        table: 'min-w-full table-fixed caption-bottom text-sm',
        cell: 'p-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        head: 'h-10 w-auto p-3 text-left align-middle tracking-tight whitespace-nowrap text-gray-900 font-medium [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        scrollIndicator:
          'absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm transition-opacity duration-200 animate-in fade-in-0',
        gradientLeft:
          'absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none z-5 animate-in fade-in-0',
        gradientRight:
          'absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-5'
      },
      ghost: {
        container: 'relative w-full max-w-full',
        scrollWrapper: 'relative overflow-x-scroll',
        body: '[&_tr:last-child]:border-0',
        row: 'hover:bg-muted/50 data-[state=selected]:bg-muted transition-colors border-b ',
        header: '[&_tr]:border-b',
        table: 'min-w-full table-fixed caption-bottom text-sm',
        cell: 'p-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        head: 'h-10 w-auto p-3 tracking-tight text-left align-middle  whitespace-nowrap font-medium text-gray-900 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        scrollIndicator:
          'absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm transition-opacity duration-200',
        gradientLeft:
          'absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white via-white/50 to-transparent pointer-events-none z-5 animate-in fade-in-0',
        gradientRight:
          'absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white via-white/50 to-transparent pointer-events-none z-5 animate-in fade-in-0'
      }
    },
    sticky: {
      true: {
        // Sticky header only works inside the table's own scroll container: the
        // scrollWrapper caps height and scrolls vertically, and the thead sticks to
        // its top edge. Document-level sticky (top-20) never worked here because the
        // overflow wrapper is the sticky containing block.
        header: 'sticky top-0 z-20',
        // The scroll region fills whatever height its flex parent gives it (see
        // DataTableWrapper's flex column in workspace skin) instead of measuring the
        // viewport itself — no guessed reserve for the toolbar/pagination chrome.
        scrollWrapper: 'overflow-y-auto min-h-0 flex-1',
        // Full-bleed surface: no card chrome, no side borders — the table spans the
        // window. Only the first/last cells carry the gutter so text still lines up
        // with the header band's title.
        container:
          'relative flex min-h-0 flex-1 w-full max-w-full flex-col border-y border-x-0 bg-white shadow-none rounded-none',
        head: '[&:first-child]:pl-8 [&:last-child]:pr-8',
        cell: '[&:first-child]:pl-8 [&:last-child]:pr-8'
      }
    },
    size: {
      sm: {
        head: 'p-1.5',
        cell: 'p-1.5',
        table: 'text-xs'
      },
      md: {
        head: 'p-3',
        cell: 'p-3'
      },
      lg: {
        head: 'px-4 py-3',
        cell: 'px-4 py-3',
        table: 'text-base'
      }
    }
  },
  defaultVariants: {
    variant: 'striped',
    size: 'md',
    sticky: false
  }
});

export interface TableProps
  extends React.HTMLAttributes<HTMLTableElement>, VariantProps<typeof tableVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

interface TableContextValue {
  variant: VariantProps<typeof tableVariants>['variant'];
  size?: VariantProps<typeof tableVariants>['size'];
  sticky?: VariantProps<typeof tableVariants>['sticky'];
}

const TableContext = React.createContext<TableContextValue | undefined>(undefined);

interface TableProviderProps {
  children: React.ReactNode;
  variant: VariantProps<typeof tableVariants>['variant'];
  size?: VariantProps<typeof tableVariants>['size'];
  sticky?: VariantProps<typeof tableVariants>['sticky'];
}

export const TableProvider: React.FC<TableProviderProps> = ({
  children,
  variant,
  size,
  sticky
}) => {
  const value = React.useMemo(
    () => ({
      variant,
      size,
      sticky
    }),
    [variant, size, sticky]
  );

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};

const useTable = (): TableContextValue => {
  const context = React.useContext(TableContext);
  if (context === undefined) {
    throw new Error('useTable must be used within a TableProvider');
  }
  return context;
};

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, children, variant, size, sticky: stickyProp, ...props }, ref) => {
    // PoC rollout gate: sticky header + viewport-fill scroll region only activate when
    // the frontend toggle is on (off by default). Consumers keep passing `sticky`.
    // Subscribed via useFrontendFeature so the in-app switch applies live.
    const { enabled: stickyTablesEnabled } = useFrontendFeature('stickyTables');
    const sticky = Boolean(stickyProp) && stickyTablesEnabled;
    const {
      container,
      table,
      scrollWrapper,
      scrollIndicator,
      gradientLeft,
      gradientRight
    } = tableVariants({ variant, size, sticky });
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [rowCadence, setRowCadence] = React.useState(36);

    // Sticky mode: the scroll region's height comes from its flex parent (see
    // DataTableWrapper's flex column in workspace skin), not from measuring the
    // viewport. Only the blank-row filler cadence still needs measuring, so it can
    // mirror the real rendered row height (an ancestor CSS `zoom` rescales px, so
    // divide by the effective zoom) and keep the filler's rhythm in step with density
    // changes.
    const measureRowCadence = React.useCallback(() => {
      const element = scrollRef.current;
      if (!element || !sticky) return;

      const firstRow = element.querySelector('tbody tr');
      if (!firstRow) return;

      const rect = element.getBoundingClientRect();
      const effectiveZoom =
        element.offsetWidth > 0 ? rect.width / element.offsetWidth : 1;
      const cadence = Math.max(
        24,
        Math.round(firstRow.getBoundingClientRect().height / (effectiveZoom || 1))
      );
      setRowCadence((prev) => (prev === cadence ? prev : cadence));
    }, [sticky]);

    // No dependency list on purpose: rows/density changes move the row height, and the
    // setter above bails out when nothing changed.
    React.useLayoutEffect(() => {
      if (!sticky) return;
      measureRowCadence();
    });

    React.useEffect(() => {
      if (!sticky) return;
      const element = scrollRef.current;
      if (!element) return;
      const resizeObserver = new ResizeObserver(measureRowCadence);
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
    }, [sticky, measureRowCadence]);
    const [showIndicators, setShowIndicators] = React.useState(false);

    const checkScrollability = React.useCallback(() => {
      const element = scrollRef.current;
      if (!element) return;

      const { scrollLeft, scrollWidth, clientWidth } = element;
      const hasOverflow = scrollWidth > clientWidth;

      // Add a threshold to prevent flickering at the edges
      const threshold = 10;
      const maxScrollLeft = scrollWidth - clientWidth;
      const isAtStart = scrollLeft <= threshold;
      const isAtEnd = scrollLeft >= maxScrollLeft - threshold;

      setShowIndicators(hasOverflow);
      setCanScrollLeft(!isAtStart && hasOverflow);
      setCanScrollRight(!isAtEnd && hasOverflow);
    }, []);

    React.useEffect(() => {
      const element = scrollRef.current;
      if (!element) return;

      checkScrollability();

      const resizeObserver = new ResizeObserver(checkScrollability);
      resizeObserver.observe(element);

      // Debounce scroll events to prevent excessive updates
      let timeoutId: NodeJS.Timeout;
      const debouncedScrollHandler = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(checkScrollability, 16); // ~60fps
      };

      element.addEventListener('scroll', debouncedScrollHandler, { passive: true });

      return () => {
        resizeObserver.disconnect();
        element.removeEventListener('scroll', debouncedScrollHandler);
        clearTimeout(timeoutId);
      };
    }, [checkScrollability]);

    const scrollLeft = () => {
      scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
    };

    const scrollRight = () => {
      scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
    };

    return (
      <TableProvider variant={variant} size={size} sticky={sticky}>
        <div
          className={cn(
            container(),
            className,
            canScrollLeft && 'border-l-0',
            canScrollRight && 'border-r-0'
          )}
        >
          {/* Left gradient and scroll indicator */}
          {showIndicators && canScrollLeft && (
            <>
              <div className={cn(gradientLeft())} />
              <button
                onClick={scrollLeft}
                className={cn(scrollIndicator(), 'left-2')}
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              </button>
            </>
          )}

          {/* Right gradient and scroll indicator */}
          {showIndicators && canScrollRight && (
            <>
              <div className={cn(gradientRight())} />
              <button
                onClick={scrollRight}
                className={cn(scrollIndicator(), 'right-2')}
                aria-label="Scroll right"
              >
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
            </>
          )}

          <div ref={scrollRef} className={cn(scrollWrapper(), sticky && 'flex flex-col')}>
            <table
              ref={ref}
              className={cn(table(), className, sticky && 'shrink-0')}
              {...props}
            >
              {children}
            </table>
            {sticky && (
              <div
                aria-hidden
                className="min-h-0 flex-1"
                style={{
                  // Blank-row filler: repeat the row border at the table's row cadence
                  // so the empty region reads as empty records, not dead space.
                  backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${rowCadence - 1}px, var(--color-border) ${rowCadence - 1}px, var(--color-border) ${rowCadence}px)`
                }}
              />
            )}
          </div>
        </div>
      </TableProvider>
    );
  }
);
Table.displayName = 'Table';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => {
  const { variant, size } = useTable();
  const { row } = tableVariants({ variant, size });
  return <tr ref={ref} className={cn(row(), className)} {...props} />;
});
TableRow.displayName = 'TableRow';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { variant, size, sticky } = useTable();
  const { header } = tableVariants({ variant, size, sticky });
  return <thead ref={ref} className={cn(header(), className)} {...props} />;
});
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { variant, size } = useTable();
  const { body } = tableVariants({ variant, size });
  return <tbody ref={ref} className={cn(body(), className)} {...props} />;
});
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { variant, size, sticky } = useTable();
  const { head } = tableVariants({ variant, size, sticky });
  return <th ref={ref} className={cn(head(), className)} {...props} />;
});
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { variant, size, sticky } = useTable();
  const { cell } = tableVariants({ variant, size, sticky });
  return <td ref={ref} className={cn(cell(), className)} {...props} />;
});
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption
};
